import React, { useState } from "react";
import PageHeader from "@/components/PageHeader";
import { useLists } from "@/context/ListsContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";

const LIST_LABELS = {
  owners: "Team Owners",
  companies: "Companies",
  priorities: "Priority Levels",
  job_statuses: "Job Statuses",
  task_statuses: "Task Statuses",
  artwork_statuses: "Artwork Statuses",
  website_statuses: "Website Statuses",
  website_categories: "Website Categories",
  sendoff_types: "Send-Off Types",
  sendoff_statuses: "Send-Off Statuses",
  quote_statuses: "Quote Statuses",
};

function ListEditor({ listType, values, onSave }) {
  const [items, setItems] = useState(values || []);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { setItems(values || []); }, [values]);

  const persist = async (next) => {
    setSaving(true);
    try {
      await api.put(`/setup/lists/${listType}`, next);
      onSave();
      toast.success(`${LIST_LABELS[listType] || listType} updated`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) { toast.error("Already in the list"); return; }
    const next = [...items, v];
    setItems(next);
    setDraft("");
    persist(next);
  };

  const remove = (val) => {
    const next = items.filter((i) => i !== val);
    setItems(next);
    persist(next);
  };

  return (
    <Card className="card-elev">
      <CardContent className="p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-heading text-xl">{LIST_LABELS[listType] || listType}</h3>
          <span className="text-xs text-muted-foreground">{items.length} items</span>
        </div>

        <div className="flex flex-wrap gap-2 mb-4 min-h-[2.25rem]">
          {items.length === 0 && <span className="text-sm text-muted-foreground">Empty</span>}
          {items.map((v) => (
            <span key={v} className="group inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background px-3 py-1 text-sm"
              data-testid={`setup-${listType}-item-${v}`}>
              {v}
              <button
                onClick={() => remove(v)}
                className="opacity-40 group-hover:opacity-100 hover:text-destructive transition-opacity"
                data-testid={`setup-${listType}-remove-${v}`}
                aria-label={`Remove ${v}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>

        <div className="flex gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="Add new value…"
            data-testid={`setup-${listType}-input`}
            className="bg-background"
            disabled={saving}
          />
          <Button onClick={add} data-testid={`setup-${listType}-add`}>
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Setup() {
  const { lists, refreshLists } = useLists();

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-10">
      <PageHeader
        eyebrow="Setup Lists"
        title="Manage Dropdowns"
        description="Everything that appears in a dropdown lives here. Add, remove, and rename to match how the studio actually works."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.keys(LIST_LABELS).map((lt) => (
          <ListEditor
            key={lt}
            listType={lt}
            values={lists[lt] || []}
            onSave={refreshLists}
          />
        ))}
      </div>
    </div>
  );
}
